/**
 * Cross-domain tracking parameter passthrough.
 *
 * captureTrackingParams() — call once on app load. Reads the tracked
 *   query params from window.location and persists them to both
 *   sessionStorage and localStorage (30-day expiry). Non-empty values
 *   overwrite; empty values never overwrite.
 *
 * decorateShopUrl(url) — call at every outbound point to an adelira.nl
 *   URL. Appends stored tracking params without overwriting existing
 *   params on the URL. If fbclid is present but utm_source is missing,
 *   also appends utm_source=facebook & utm_medium=paid.
 */

const STORAGE_KEY = 'adelira_tracking';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const TRACKED_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'fbclid',
  'gclid',
  'msclkid',
  'ttclid',
] as const;

type TrackedKey = (typeof TRACKED_KEYS)[number];
type TrackingData = Partial<Record<TrackedKey, string>>;

interface StoredEnvelope {
  data: TrackingData;
  expiresAt: number;
}

function readLocalStorage(): TrackingData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredEnvelope;
    if (!parsed || typeof parsed !== 'object') return {};
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return {};
    }
    return parsed.data || {};
  } catch {
    return {};
  }
}

function readSessionStorage(): TrackingData {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return (JSON.parse(raw) as TrackingData) || {};
  } catch {
    return {};
  }
}

function persist(data: TrackingData): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
  try {
    const envelope: StoredEnvelope = {
      data,
      expiresAt: Date.now() + THIRTY_DAYS_MS,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // ignore
  }
}

/** Merge — non-empty incoming values overwrite; empty never overwrites. */
function mergePreferNonEmpty(
  base: TrackingData,
  incoming: TrackingData,
): TrackingData {
  const out: TrackingData = { ...base };
  for (const key of TRACKED_KEYS) {
    const v = incoming[key];
    if (typeof v === 'string' && v.length > 0) {
      out[key] = v;
    }
  }
  return out;
}

/** Read stored tracking params (localStorage wins over sessionStorage on conflict). */
export function getStoredTracking(): TrackingData {
  if (typeof window === 'undefined') return {};
  return mergePreferNonEmpty(readSessionStorage(), readLocalStorage());
}

/**
 * Capture tracked params from the current URL and persist.
 * Safe to call multiple times; only non-empty values overwrite.
 */
export function captureTrackingParams(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const incoming: TrackingData = {};
  for (const key of TRACKED_KEYS) {
    const v = params.get(key);
    if (v && v.length > 0) incoming[key] = v;
  }

  const existing = getStoredTracking();
  const merged = mergePreferNonEmpty(existing, incoming);
  persist(merged);
}

/** True if the URL targets an adelira.nl host (any subdomain). */
function isAdeliraUrl(u: URL): boolean {
  const host = u.hostname.toLowerCase();
  return host === 'adelira.nl' || host.endsWith('.adelira.nl');
}

/**
 * Append stored tracking params to an outbound adelira.nl URL.
 * - Never overwrites params already present on the destination URL.
 * - If fbclid is present but utm_source is missing (on both dest and stored),
 *   appends utm_source=facebook & utm_medium=paid as a fallback signal.
 * - If the visitor is in the Instagram/Facebook in-app browser and utm_source
 *   is still missing, appends utm_source=facebook & utm_medium=paid_social.
 * - No-op for non-adelira.nl URLs and for malformed inputs.
 */
export function decorateShopUrl(destinationUrl: string): string {
  if (typeof window === 'undefined' || !destinationUrl) return destinationUrl;

  let url: URL;
  try {
    url = new URL(destinationUrl, window.location.origin);
  } catch {
    return destinationUrl;
  }

  if (!isAdeliraUrl(url)) return destinationUrl;

  const stored = getStoredTracking();

  for (const [key, value] of Object.entries(stored)) {
    if (!value) continue;
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  }

  // Fallback: fbclid without utm_source → tag as paid facebook
  const hasFbclid = url.searchParams.has('fbclid');
  const hasUtmSource = url.searchParams.has('utm_source');
  if (hasFbclid && !hasUtmSource) {
    url.searchParams.set('utm_source', 'facebook');
    if (!url.searchParams.has('utm_medium')) {
      url.searchParams.set('utm_medium', 'paid');
    }
  }

  // Fallback: Meta in-app browser (stripped query string) → tag as paid social
  if (
    typeof navigator !== 'undefined' &&
    /Instagram|FBAN|FBAV|FB_IAB/i.test(navigator.userAgent) &&
    !url.searchParams.has('utm_source')
  ) {
    url.searchParams.set('utm_source', 'facebook');
    if (!url.searchParams.has('utm_medium')) {
      url.searchParams.set('utm_medium', 'paid_social');
    }
  }

  return url.toString();
}